import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { isEmpty, isPresent } from '@ember/utils'
import type Store from '@ember-data/store'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { IntlService } from 'ember-intl'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type SessionService from 'teacher-ui-client/services/session'
import { Revealer, type RevealerEvent } from 'teacher-ui-client/components/base/revealer'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { pick } from 'teacher-ui-client/helpers/pick'
import UiButton from 'teacher-ui-client/components/ui-button'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import onOutside from 'teacher-ui-client/modifiers/on-outside'
import autoFocus from 'teacher-ui-client/modifiers/auto-focus'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { PhosphorIconName } from 'phosphor-icons'

interface Signature {
  Args: {
    collection?: BookLibraryCollection
    booksForNewCollection?: BookLibraryBook[]
    onSuccess?: (collection: BookLibraryCollection) => unknown
    onFail?: (collection: BookLibraryCollection, saveError: unknown) => unknown
    prefixIcon: PhosphorIconName
    buttonIcon: PhosphorIconName
  }
  Blocks: {
    default: []
  }
}

interface APIError {
  code?: string
  title?: string
}

interface SaveError {
  errors: APIError[]
}

function isSaveError(maybeSaveError: unknown): maybeSaveError is SaveError {
  const isObject = typeof maybeSaveError === 'object' && maybeSaveError !== null
  if (isObject && 'errors' in maybeSaveError && Array.isArray(maybeSaveError.errors)) {
    return (maybeSaveError as SaveError).errors.length > 0
  }
  return false
}

export class BookLibraryCollectionInlineEdit extends Component<Signature> {
  @service store!: Store

  @service intl!: IntlService

  @service session!: SessionService

  @tracked collection?: BookLibraryCollection

  @tracked validationText?: string

  themeKey = ThemeKeys.blue

  @action
  onReveal(_event: RevealerEvent) {
    this.resetCollection()
    const { booksForNewCollection: books = [] } = this.args
    this.collection =
      this.args.collection ??
      this.store.createRecord<BookLibraryCollection>('book-library/collection', {
        name: '',
        books,
        teacher: this.session.currentTeacher,
      })
  }

  @action
  updateCollectionName(value: string) {
    if (this.collection) {
      this.collection.name = value
      this.validationText = undefined
    }
  }

  get disableCommit() {
    const isSaving = Boolean(this.collection?.isSaving)
    const hasValidationIssue = isPresent(this.validationText)
    const hasNoName = isEmpty(this.collection?.name.trim())
    const isExistingAndPristine = this.collection && !this.collection.isNew && !this.collection.hasDirtyAttributes
    return isSaving || hasValidationIssue || hasNoName || isExistingAndPristine
  }

  @action
  async onCommit(concealAction: (event: RevealerEvent) => void, event: Event) {
    const {
      collection,
      disableCommit,
      args: { onSuccess, onFail },
    } = this

    event.preventDefault()
    if (!collection || disableCommit) return

    try {
      await collection.save()

      concealAction(event as RevealerEvent)
      onSuccess?.(collection)
    } catch (saveError: unknown) {
      let handled = false
      if (isSaveError(saveError)) {
        // "soft fail" on known validation errors, and prompt them to try another name
        if (saveError.errors.some((error) => error.code === 'length')) {
          // name is too long
          this.validationText = this.intl.t('bookLibrary.components.collectionInlineEdit.nameTooLongTooltip')
          handled = true
        } else if (saveError.errors.some((error) => error.title?.includes('taken'))) {
          // name is already taken
          this.validationText = this.intl.t('bookLibrary.components.collectionInlineEdit.nameTakenTooltip')
          handled = true
        }
      }
      if (!handled) onFail?.(collection, saveError)
    }
  }

  @action
  resetCollection(_event?: RevealerEvent) {
    // discards unsaved changes. If new, removes from the store.
    if (this.collection && !this.collection.isSaving && !this.collection.isDestroyed && !this.collection.isDestroying) {
      if (this.collection.get('isNew')) {
        this.collection.unloadRecord()
      } else {
        this.collection.rollbackAttributes()
      }
    }
    this.collection = undefined
    this.validationText = undefined
  }

  willDestroy() {
    super.willDestroy()
    this.resetCollection()
  }

  <template>
    <Revealer @onReveal={{this.onReveal}} @onConceal={{this.resetCollection}}>
      <:concealed as |revealAction|>
        <button
          data-test-concealed-block
          class="flex cursor-pointer flex-nowrap items-center text-left leading-10"
          type="button"
          {{!
      For most browsers, only the focusin listener is needed (will work for both clicks, and tabbing). However, Safari
      refuses to apply focus to a clicked button. Having both does not cause issues on other browsers.
      https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
       }}
          {{on "focusin" revealAction}}
          {{on "click" revealAction}}
        >
          <PhosphorIcon @icon={{@prefixIcon}} class="text-oceany-blue-350 mr-2 print:hidden" />
          {{yield}}
        </button>
      </:concealed>
      <:revealed as |concealAction|>
        <form
          data-test-revealed-block
          class="flex flex-nowrap items-center"
          {{onOutside "click" concealAction}}
          {{onOutside "focusin" concealAction}}
          {{on "submit" (fn this.onCommit concealAction)}}
        >
          <PhosphorIcon @icon={{@prefixIcon}} class="text-oceany-blue-350 mr-2" />
          <InputText
            type="text"
            class="mr-1 grow"
            value={{this.collection.name}}
            placeholder={{t "bookLibrary.components.collectionSelect.createPlaceholder"}}
            aria-label={{t "bookLibrary.components.collectionSelect.createPlaceholder"}}
            @themeKey={{this.themeKey}}
            {{autoFocus}}
            {{on "input" (pick "target.value" this.updateCollectionName)}}
          />
          <UiButton
            data-test-collection-edit
            disabled={{this.disableCommit}}
            aria-label={{t "bookLibrary.components.collectionSelect.createButtonAriaLabel"}}
            type="submit"
            @appearance={{ButtonAppearance.regular}}
          >
            <PhosphorIcon @icon={{@buttonIcon}} />
          </UiButton>
          {{#if this.validationText}}
            <Tooltip @text={{this.validationText}} @showOnRender={{true}} data-test-validation-tooltip />
          {{/if}}
        </form>
      </:revealed>
    </Revealer>
  </template>
}

export default BookLibraryCollectionInlineEdit
